import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock,
  SignOut,
  Star,
  CalendarBlank,
  Check,
  X,
  Trash,
  Plus,
  CaretLeft,
  CaretRight,
  Warning,
  CheckCircle,
  Clock,
  User,
  Phone,
  CurrencyDollar,
  ChartBar,
  Users,
  CalendarCheck,
  XCircle,
  Eye,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { toast } from "sonner";
import { format, addMonths, subMonths, addDays, startOfDay } from "date-fns";
import { es } from "date-fns/locale";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth helper functions
const getToken = () => localStorage.getItem("admin_token");
const setToken = (token) => localStorage.setItem("admin_token", token);
const removeToken = () => localStorage.removeItem("admin_token");

const authHeader = () => ({
  headers: { Authorization: `Bearer ${getToken()}` }
});

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loginData, setLoginData] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  
  // Dashboard state
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [reviews, setReviews] = useState([]);
  
  // Calendar state
  const [selectedDates, setSelectedDates] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [blockReason, setBlockReason] = useState("Mantenimiento");
  const [actionLoading, setActionLoading] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyAuth = async () => {
      const token = getToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        await axios.get(`${API}/auth/verify`, authHeader());
        setIsAuthenticated(true);
      } catch (error) {
        removeToken();
      } finally {
        setIsLoading(false);
      }
    };

    verifyAuth();
  }, []);

  // Fetch data when authenticated
  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const [statsRes, reservationsRes, blockedRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeader()),
        axios.get(`${API}/reservations`, authHeader()),
        axios.get(`${API}/blocked-dates`, authHeader()),
        axios.get(`${API}/reviews/all`, authHeader()),
      ]);

      setStats(statsRes.data);
      setReservations(reservationsRes.data);
      setBlockedDates(blockedRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
      console.error("Error fetching data:", error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      setToken(response.data.token);
      setIsAuthenticated(true);
      toast.success("¡Bienvenido al panel de administración!");
    } catch (error) {
      setLoginError(error.response?.data?.detail || "Credenciales incorrectas");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, authHeader());
    } catch (e) {
      // Ignore errors
    }
    removeToken();
    setIsAuthenticated(false);
    setStats(null);
    setReservations([]);
    toast.info("Sesión cerrada");
  };

  // Admin Actions
  const blockDates = async () => {
    if (selectedDates.length === 0) {
      toast.error("Selecciona al menos una fecha");
      return;
    }

    setActionLoading(true);
    let blocked = 0;

    try {
      for (const date of selectedDates) {
        const dateStr = format(date, "yyyy-MM-dd");
        try {
          await axios.post(`${API}/blocked-dates`, { date: dateStr, reason: blockReason }, authHeader());
          blocked++;
        } catch (e) {
          // Date might already be blocked
        }
      }
      toast.success(`${blocked} fecha(s) bloqueada(s)`);
      setSelectedDates([]);
      fetchData();
    } catch (error) {
      toast.error("Error al bloquear fechas");
    } finally {
      setActionLoading(false);
    }
  };

  const unblockDate = async (dateStr) => {
    try {
      await axios.delete(`${API}/blocked-dates/${dateStr}`, authHeader());
      toast.success("Fecha desbloqueada");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al desbloquear fecha");
    }
  };

  const cancelReservation = async (reservationId) => {
    if (!window.confirm("¿Estás seguro de ELIMINAR esta reservación? Esta acción no se puede deshacer.")) return;

    try {
      await axios.delete(`${API}/reservations/${reservationId}`, authHeader());
      toast.success("Reservación eliminada");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar reservación");
    }
  };

  const updatePaymentStatus = async (reservationId, status) => {
    try {
      await axios.patch(`${API}/reservations/${reservationId}/payment`, { payment_status: status }, authHeader());
      toast.success("Estado de pago actualizado");
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar estado de pago");
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm("¿Eliminar esta reseña?")) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, authHeader());
      toast.success("Reseña eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar reseña");
    }
  };

  // Date helpers
  const isDateBlocked = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return blockedDates.some((d) => d.date === dateStr && d.type === "blocked");
  };

  const isDateReserved = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return blockedDates.some((d) => d.date === dateStr && d.type === "reserved");
  };

  const getDateStatus = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const blocked = blockedDates.find((d) => d.date === dateStr);
    return blocked?.type || "available";
  };

  // Loading screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E05A47]"></div>
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-[#E05A47] to-[#C84938] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Lock className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-['Plus_Jakarta_Sans'] text-2xl font-bold text-stone-900">
              Panel de Administración
            </h1>
            <p className="text-stone-500 mt-2">Tu Hogar en Tampico</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña de Propietario</Label>
              <Input
                id="password"
                type="password"
                placeholder="Ingresa tu contraseña"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, username: "propietario", password: e.target.value })}
                className="h-12 rounded-xl text-center tracking-widest"
                data-testid="admin-password-input"
              />
            </div>

            {loginError && (
              <div className="bg-red-50 border border-red-200 text-red-600 text-sm p-3 rounded-xl flex items-center gap-2">
                <Warning className="w-4 h-4" />
                {loginError}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 bg-[#E05A47] hover:bg-[#C84938] rounded-xl font-semibold"
              data-testid="admin-login-btn"
            >
              Iniciar Sesión
            </Button>
          </form>

          <p className="text-center text-xs text-stone-400 mt-6">
            Acceso restringido solo para propietarios
          </p>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#E05A47] to-[#C84938] rounded-xl flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-['Plus_Jakarta_Sans'] text-lg font-bold text-stone-900">
                Panel Admin
              </h1>
              <p className="text-xs text-stone-500">Tu Hogar en Tampico</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={fetchData}
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="refresh-btn"
            >
              <ArrowClockwise className="w-4 h-4" />
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="rounded-full text-red-500 border-red-200 hover:bg-red-50"
              data-testid="admin-logout-btn"
            >
              <SignOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: "dashboard", label: "Dashboard", icon: ChartBar },
              { id: "reservations", label: "Reservaciones", icon: CalendarCheck, badge: reservations.filter(r => r.status === "confirmed").length },
              { id: "calendar", label: "Calendario", icon: CalendarBlank },
              { id: "reviews", label: "Reseñas", icon: Star, badge: reviews.length },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-4 font-medium text-sm border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
                  activeTab === tab.id
                    ? "border-[#E05A47] text-[#E05A47]"
                    : "border-transparent text-stone-500 hover:text-stone-700"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className="bg-[#E05A47] text-white text-xs px-2 py-0.5 rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Dashboard Tab */}
        {activeTab === "dashboard" && stats && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <CalendarCheck className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-sm text-stone-500">Reservaciones Activas</span>
                </div>
                <p className="text-3xl font-bold text-stone-900">{stats.reservations.active}</p>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-sm text-stone-500">Pagos Pendientes</span>
                </div>
                <p className="text-3xl font-bold text-stone-900">{stats.payments.pending}</p>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <CurrencyDollar className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm text-stone-500">Ingresos Totales</span>
                </div>
                <p className="text-3xl font-bold text-green-600">${stats.revenue.total.toLocaleString()}</p>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <span className="text-sm text-stone-500">Fechas Bloqueadas</span>
                </div>
                <p className="text-3xl font-bold text-stone-900">{stats.dates.blocked}</p>
              </div>
            </div>

            {/* Quick Overview */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent Reservations */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h3 className="font-semibold text-stone-900">Reservaciones Recientes</h3>
                </div>
                <div className="p-4">
                  {reservations.slice(0, 5).map((res) => (
                    <div key={res.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                      <div>
                        <p className="font-medium text-stone-800">{res.name}</p>
                        <p className="text-sm text-stone-500">
                          {format(new Date(res.check_in), "dd MMM", { locale: es })} - {format(new Date(res.check_out), "dd MMM", { locale: es })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          res.status === "confirmed" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                        }`}>
                          {res.status === "confirmed" ? "Confirmada" : "Cancelada"}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          res.payment_status === "paid" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {res.payment_status === "paid" ? "Pagado" : "Pendiente"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Calendar Preview */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h3 className="font-semibold text-stone-900">Vista del Calendario</h3>
                </div>
                <div className="p-4 flex justify-center">
                  <CalendarComponent
                    mode="single"
                    month={currentMonth}
                    onMonthChange={setCurrentMonth}
                    locale={es}
                    modifiers={{
                      blocked: (date) => isDateBlocked(date),
                      reserved: (date) => isDateReserved(date),
                    }}
                    modifiersStyles={{
                      blocked: { backgroundColor: "#E5E7EB", color: "#6B7280" },
                      reserved: { backgroundColor: "#FEE2E2", color: "#DC2626" },
                    }}
                    className="rounded-xl"
                  />
                </div>
                <div className="px-6 pb-4 flex gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
                    <span className="text-stone-600">Disponible</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-red-100 border border-red-300"></div>
                    <span className="text-stone-600">Reservado</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-gray-200 border border-gray-300"></div>
                    <span className="text-stone-600">Bloqueado</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reservations Tab */}
        {activeTab === "reservations" && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-stone-900">Todas las Reservaciones ({reservations.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-stone-500 uppercase">Huésped</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-stone-500 uppercase">Contacto</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-stone-500 uppercase">Fechas</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-stone-500 uppercase">Huéspedes</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-stone-500 uppercase">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-stone-500 uppercase">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-stone-500 uppercase">Pago</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-stone-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reservations.map((res) => (
                    <tr key={res.id} className={res.status === "cancelled" ? "bg-gray-50 opacity-60" : ""}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-stone-500" />
                          </div>
                          <div>
                            <p className="font-medium text-stone-800">{res.name}</p>
                            <p className="text-xs text-stone-500">{res.nights} noche(s)</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-stone-800">{res.phone}</p>
                        {res.email && <p className="text-xs text-stone-500">{res.email}</p>}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-stone-800">
                          {format(new Date(res.check_in), "dd MMM yyyy", { locale: es })}
                        </p>
                        <p className="text-xs text-stone-500">
                          al {format(new Date(res.check_out), "dd MMM yyyy", { locale: es })}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="flex items-center gap-1 text-sm text-stone-800">
                          <Users className="w-4 h-4" />
                          {res.guests}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-semibold text-stone-800">${res.total.toLocaleString()}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          res.status === "confirmed" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                        }`}>
                          {res.status === "confirmed" ? "Confirmada" : "Cancelada"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {res.status === "confirmed" && (
                          <select
                            value={res.payment_status}
                            onChange={(e) => updatePaymentStatus(res.id, e.target.value)}
                            className={`px-3 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${
                              res.payment_status === "paid" 
                                ? "bg-blue-100 text-blue-700" 
                                : res.payment_status === "refunded"
                                ? "bg-purple-100 text-purple-700"
                                : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            <option value="pending">Pendiente</option>
                            <option value="paid">Pagado</option>
                            <option value="refunded">Reembolsado</option>
                          </select>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <Button
                          onClick={() => cancelReservation(res.id)}
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:bg-red-50 rounded-full"
                        >
                          <Trash className="w-4 h-4 mr-1" />
                          Eliminar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {reservations.length === 0 && (
                <div className="text-center py-12 text-stone-500">
                  No hay reservaciones aún
                </div>
              )}
            </div>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === "calendar" && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Block Dates */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#E05A47]" />
                Bloquear Fechas
              </h3>
              <p className="text-sm text-stone-500 mb-4">
                Selecciona las fechas que deseas bloquear para que no puedan ser reservadas.
              </p>

              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <CaretLeft className="w-5 h-5" />
                </button>
                <span className="font-semibold text-stone-800">
                  {format(currentMonth, "MMMM yyyy", { locale: es })}
                </span>
                <button
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <CaretRight className="w-5 h-5" />
                </button>
              </div>

              <CalendarComponent
                mode="multiple"
                selected={selectedDates}
                onSelect={setSelectedDates}
                month={currentMonth}
                onMonthChange={setCurrentMonth}
                locale={es}
                disabled={(date) => isDateReserved(date)}
                modifiers={{
                  blocked: (date) => isDateBlocked(date),
                  reserved: (date) => isDateReserved(date),
                }}
                modifiersStyles={{
                  blocked: { backgroundColor: "#E5E7EB", color: "#6B7280" },
                  reserved: { backgroundColor: "#FEE2E2", color: "#DC2626", cursor: "not-allowed" },
                }}
                className="rounded-xl"
              />

              {/* Block Controls */}
              <div className="mt-6 space-y-4">
                <div className="space-y-2">
                  <Label>Motivo del bloqueo</Label>
                  <Input
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    placeholder="Ej: Mantenimiento, Uso personal, etc."
                    className="rounded-xl"
                  />
                </div>
                <Button
                  onClick={blockDates}
                  disabled={selectedDates.length === 0 || actionLoading}
                  className="w-full bg-[#E05A47] hover:bg-[#C84938] rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Bloquear {selectedDates.length} fecha(s)
                </Button>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
                  <span className="text-stone-600">Disponible</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-[#E05A47]"></div>
                  <span className="text-stone-600">Seleccionado</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-100 border border-red-300"></div>
                  <span className="text-stone-600">Reservado</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-gray-200 border border-gray-300"></div>
                  <span className="text-stone-600">Bloqueado</span>
                </div>
              </div>
            </div>

            {/* Blocked Dates List */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-semibold text-stone-900 mb-4">
                Fechas Bloqueadas ({blockedDates.filter(d => d.type === "blocked").length})
              </h3>

              {blockedDates.filter(d => d.type === "blocked").length === 0 ? (
                <div className="text-center py-12">
                  <CalendarBlank className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">No hay fechas bloqueadas manualmente</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {blockedDates
                    .filter(d => d.type === "blocked")
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((blocked) => (
                      <div
                        key={blocked.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                      >
                        <div>
                          <p className="font-medium text-stone-800">
                            {format(new Date(blocked.date), "EEEE, d 'de' MMMM", { locale: es })}
                          </p>
                          <p className="text-sm text-stone-500">{blocked.reason}</p>
                        </div>
                        <Button
                          onClick={() => unblockDate(blocked.date)}
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:bg-red-50 rounded-full"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                </div>
              )}

              {/* Reserved Dates */}
              <h3 className="font-semibold text-stone-900 mt-8 mb-4">
                Fechas Reservadas ({blockedDates.filter(d => d.type === "reserved").length})
              </h3>
              {blockedDates.filter(d => d.type === "reserved").length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-stone-500">No hay reservaciones activas</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {blockedDates
                    .filter(d => d.type === "reserved")
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((reserved) => (
                      <div
                        key={reserved.id}
                        className="flex items-center justify-between p-3 bg-red-50 rounded-xl"
                      >
                        <div>
                          <p className="font-medium text-red-800">
                            {format(new Date(reserved.date), "EEEE, d 'de' MMMM", { locale: es })}
                          </p>
                          <p className="text-sm text-red-600">{reserved.reason}</p>
                        </div>
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                          Reservado
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === "reviews" && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-stone-900">Todas las Reseñas ({reviews.length})</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {reviews.map((review) => (
                <div key={review.id} className="p-6 flex gap-4">
                  <img
                    src={review.avatar}
                    alt={review.name}
                    className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                  />
                  <div className="flex-grow">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-stone-800">{review.name}</p>
                        <div className="flex gap-0.5 mt-1">
                          {[...Array(review.rating)].map((_, i) => (
                            <Star key={i} weight="fill" className="w-4 h-4 text-amber-400" />
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          review.approved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {review.approved ? "Publicada" : "Pendiente"}
                        </span>
                        <Button
                          onClick={() => deleteReview(review.id)}
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:bg-red-50 rounded-full"
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-stone-600 mt-3">{review.comment}</p>
                  </div>
                </div>
              ))}
              {reviews.length === 0 && (
                <div className="text-center py-12 text-stone-500">
                  No hay reseñas aún
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
